import { useEffect, useMemo, useState } from 'react';
import {
  sheetUrl,
  byIdUrl,
  searchUrl,
  ALLERGY_PROFILES_SHEET,
  MEMBERS_SHEET,
} from './config';
import { tokens } from './theme';
import {
  CHIP_OPTIONS,
  ROLE_OPTIONS,
  SEVERITY_OPTIONS,
  parseChips,
  serializeChips,
  makeProfileId,
  nowIso,
  roleLabel,
  severityPillStyle,
} from './allergies';

// AllergyManager
// ----------------------------------------------------------------------------
// Self-serve CRUD for the host member's "household + guests with allergies"
// roster. Replaces the old single-allergy-per-member model.
//
// Props:
//   memberNumber   string — the host's member number
//   memberName     string — the host's name (used to seed the `self` row)
//   theme          'dark' | 'light'
//
// Behavior:
//   • On first mount, fetch existing profiles for this host.
//   • If there are no profiles BUT the legacy Members.allergy / allergyNote is
//     non-empty, migrate that into a `self` row (one-time per host).
//   • If there's still no `self` row after migration, seed an empty one so
//     the member's own profile is always pinned to the top of the list.
//   • Self entries are user-editable in name (the user told us so explicitly:
//     accounting is keyed on memberNumber, not name text).

export default function AllergyManager({ memberNumber, memberName, theme }) {
  const t = tokens(theme);
  const dark = theme === 'dark';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [profiles, setProfiles] = useState([]);

  // Edit form state — null when nothing is being edited
  const [editing, setEditing] = useState(null);
  // Shape: { id, name, role, allergies: string[], note, severity, isNew }

  // ===== Loaders =====

  const fetchProfiles = async () => {
    if (!memberNumber) {
      setProfiles([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setStatus('');
    try {
      const url = searchUrl(ALLERGY_PROFILES_SHEET, { hostMemberNumber: memberNumber });
      const res = await fetch(url, { cache: 'no-store' });
      // SheetDB returns 404 or empty array if no rows — handle both
      let data = [];
      if (res.ok) {
        data = await res.json();
      } else if (res.status !== 404) {
        throw new Error(`Profiles fetch failed: ${res.status}`);
      }

      const list = (data || []).map((r) => ({
        id: String(r.id || '').trim() || makeProfileId(),
        hostMemberNumber: String(r.hostMemberNumber || memberNumber),
        name: String(r.name || '').trim(),
        role: (r.role || 'self').toString().toLowerCase(),
        allergies: parseChips(r.allergies),
        note: String(r.note || ''),
        severity: (r.severity || 'mild').toString().toLowerCase(),
        addedAt: r.addedAt || '',
      }));

      // Legacy migration: if no profiles yet, peek at Members.allergy / allergyNote.
      if (list.length === 0) {
        await maybeMigrateLegacy(list);
      }

      // Pin self entry to top, then by role order, then by name
      list.sort((a, b) => {
        const ra = a.role === 'self' ? 0 : a.role === 'spouse' ? 1 : a.role === 'child' ? 2 : 3;
        const rb = b.role === 'self' ? 0 : b.role === 'spouse' ? 1 : b.role === 'child' ? 2 : 3;
        if (ra !== rb) return ra - rb;
        return (a.name || '').localeCompare(b.name || '');
      });

      // If still no self row, seed an empty placeholder (only saved when user clicks Save)
      const hasSelf = list.some((p) => p.role === 'self');
      if (!hasSelf) {
        list.unshift({
          id: makeProfileId(),
          hostMemberNumber: memberNumber,
          name: memberName || '',
          role: 'self',
          allergies: [],
          note: '',
          severity: 'none',
          addedAt: '',
          _seeded: true, // not yet saved to the sheet
        });
      }

      setProfiles(list);
    } catch (e) {
      console.error('AllergyManager: fetch failed', e);
      setStatus('Failed to load allergy profiles.');
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  };

  // Reads legacy Members.allergy and Members.allergyNote and, if present,
  // creates a `self` row in AllergyProfiles. Mutates the passed-in list.
  const maybeMigrateLegacy = async (list) => {
    try {
      const url = searchUrl(MEMBERS_SHEET, { memberNumber });
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      const row = (data || [])[0];
      if (!row) return;

      const oldChips = parseChips(row.allergy);
      const oldNote = String(row.allergyNote || '').trim();
      if (oldChips.length === 0 && !oldNote) return; // nothing to migrate

      const seeded = {
        id: makeProfileId(),
        hostMemberNumber: memberNumber,
        name: memberName || '',
        role: 'self',
        allergies: oldChips,
        note: oldNote,
        severity: 'mild', // legacy data didn't carry severity; default to mild
        addedAt: nowIso(),
      };
      try {
        const postRes = await fetch(sheetUrl(ALLERGY_PROFILES_SHEET), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            data: [
              {
                ...seeded,
                allergies: serializeChips(seeded.allergies),
              },
            ],
          }),
        });
        if (!postRes.ok) {
          console.warn('AllergyManager: legacy migration POST failed', postRes.status);
          return;
        }
        list.push(seeded);
      } catch (e) {
        console.warn('AllergyManager: legacy migration failed', e);
      }
    } catch (e) {
      console.warn('AllergyManager: legacy peek failed', e);
    }
  };

  useEffect(() => {
    fetchProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberNumber]);

  // ===== Editing =====

  const startEdit = (p) => {
    setEditing({
      ...p,
      allergies: [...(p.allergies || [])],
      isNew: false,
    });
  };

  const startNew = () => {
    setEditing({
      id: makeProfileId(),
      hostMemberNumber: memberNumber,
      name: '',
      role: 'guest',
      allergies: [],
      note: '',
      severity: 'mild',
      addedAt: '',
      isNew: true,
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setStatus('');
  };

  const toggleChip = (chip) => {
    setEditing((cur) => {
      if (!cur) return cur;
      const has = cur.allergies.includes(chip);
      return {
        ...cur,
        allergies: has ? cur.allergies.filter((c) => c !== chip) : [...cur.allergies, chip],
      };
    });
  };

  const saveEntry = async () => {
    if (!editing) return;
    const name = (editing.name || '').trim();
    if (!name) {
      setStatus('Name is required.');
      return;
    }
    if (editing.allergies.length === 0 && !(editing.note || '').trim()) {
      const ok = window.confirm(
        'No allergies or notes recorded for this person. Save anyway?\n\n' +
        '(Saving an empty profile is fine if you want to track that someone has been considered and has nothing to flag.)'
      );
      if (!ok) return;
    }

    setSaving(true);
    setStatus('Saving…');

    const payload = {
      id: editing.id,
      hostMemberNumber: editing.hostMemberNumber || memberNumber,
      name,
      role: editing.role || 'guest',
      allergies: serializeChips(editing.allergies),
      note: (editing.note || '').trim(),
      severity: editing.severity || 'mild',
      addedAt: editing.addedAt || nowIso(),
    };

    // POST when this row has never been written to the sheet (a brand-new
    // entry, or the locally-seeded `self` placeholder). Otherwise PATCH by id.
    // If a PATCH succeeds with `updated: 0` (id not found in the sheet), fall
    // back to POST so the edit isn't silently lost.
    const isCreating = !!editing.isNew || !!editing._seeded;

    const postRow = async () => {
      const res = await fetch(sheetUrl(ALLERGY_PROFILES_SHEET), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: [payload] }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`POST ${res.status}${body ? ': ' + body.slice(0, 200) : ''}`);
      }
    };

    try {
      if (isCreating) {
        await postRow();
      } else {
        const res = await fetch(byIdUrl(ALLERGY_PROFILES_SHEET, editing.id), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: payload }),
        });
        if (!res.ok) {
          const body = await res.text().catch(() => '');
          throw new Error(`PATCH ${res.status}${body ? ': ' + body.slice(0, 200) : ''}`);
        }
        // SheetDB returns 200 with { updated: 0 } when no row matches the id.
        // Treat that as "row missing, fall back to POST".
        const body = await res.clone().json().catch(() => null);
        if (body && typeof body.updated === 'number' && body.updated === 0) {
          console.warn('AllergyManager: PATCH updated 0 rows; falling back to POST');
          await postRow();
        }
      }

      setStatus('Saved.');
      setEditing(null);
      await fetchProfiles();
    } catch (e) {
      console.error('AllergyManager: save failed', e);
      setStatus(`Save failed: ${e?.message || 'unknown error'}`);
      alert(`Failed to save profile.\n\n${e?.message || ''}`);
    } finally {
      setSaving(false);
    }
  };

  const deleteEntry = async (p) => {
    if (p.role === 'self') {
      alert(
        "You can't delete your own profile entry. Edit it instead — clear chips and notes if you have no allergies."
      );
      return;
    }
    if (!window.confirm(`Remove ${p.name}'s allergy profile?`)) return;
    try {
      // If it was only seeded locally and never saved to the sheet, just drop it.
      if (p._seeded) {
        setProfiles((prev) => prev.filter((x) => x.id !== p.id));
        return;
      }
      const res = await fetch(byIdUrl(ALLERGY_PROFILES_SHEET, p.id), { method: 'DELETE' });
      if (!res.ok) throw new Error(await res.text());
      await fetchProfiles();
    } catch (e) {
      console.error('AllergyManager: delete failed', e);
      alert('Failed to remove profile.\n\n' + (e?.message || ''));
    }
  };

  // ===== List =====

  const peopleCount = useMemo(
    () => profiles.filter((p) => (p.allergies && p.allergies.length) || (p.note || '').trim()).length,
    [profiles]
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h3 style={{ marginTop: 0, marginBottom: 4, color: t.textStrong }}>
            Food Allergies — Household & Guests
          </h3>
          <div style={{ fontSize: 13, color: t.muted }}>
            {loading
              ? 'Loading…'
              : peopleCount === 0
              ? 'Nobody on file has flagged any allergies.'
              : `${peopleCount} ${peopleCount === 1 ? 'person' : 'people'} with allergies on file.`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={fetchProfiles}
            disabled={loading || saving}
            style={{
              padding: '8px 12px',
              borderRadius: 10,
              border: `1px solid ${t.inputBorder}`,
              background: t.inputBg,
              color: t.text,
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            Refresh
          </button>
          <button
            onClick={startNew}
            disabled={loading || saving}
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              border: 'none',
              background: t.accent,
              color: 'white',
              fontWeight: 800,
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            + Add person
          </button>
        </div>
      </div>

      {status && (
        <div style={{ marginTop: 8, fontSize: 12, color: t.muted }}>{status}</div>
      )}

      <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
        {profiles.map((p) => (
          <div
            key={p.id}
            style={{
              border: `1px solid ${t.border}`,
              borderRadius: 12,
              padding: 12,
              background: t.cardAlt,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 800, color: t.textStrong, fontSize: 16 }}>
                  {p.name || <span style={{ color: t.muted }}>(no name yet)</span>}
                </div>
                <div style={{ fontSize: 12, color: t.muted, marginTop: 2 }}>
                  {roleLabel(p.role)}
                  {p._seeded ? ' · not yet saved' : ''}
                </div>
                <div style={{ marginTop: 6 }}>
                  {(() => {
                    const empty =
                      (!p.allergies || p.allergies.length === 0) && !(p.note || '').trim();
                    if (empty) {
                      return (
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '2px 10px',
                            borderRadius: 999,
                            border: `1px dashed ${t.inputBorder}`,
                            color: t.muted,
                            fontSize: 12,
                          }}
                        >
                          No allergies
                        </span>
                      );
                    }
                    const sty = severityPillStyle(p.severity, dark);
                    return (
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 10px',
                          borderRadius: 999,
                          border: `1px solid ${sty.borderColor}`,
                          background: sty.background,
                          color: sty.color,
                          fontSize: 12,
                          fontWeight: sty.fontWeight,
                        }}
                      >
                        {p.severity === 'severe' ? '🚨 Severe' : '⚠ Mild'}
                      </span>
                    );
                  })()}
                </div>
                {(p.allergies?.length > 0 || (p.note || '').trim()) && (
                  <div style={{ marginTop: 6, fontSize: 13 }}>
                    {p.allergies?.length > 0 && <div>{serializeChips(p.allergies)}</div>}
                    {(p.note || '').trim() && (
                      <div style={{ color: t.muted, marginTop: 2, whiteSpace: 'pre-wrap' }}>
                        {p.note}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={() => startEdit(p)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 10,
                    border: `1px solid ${t.inputBorder}`,
                    background: t.inputBg,
                    color: t.text,
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                >
                  Edit
                </button>
                {p.role !== 'self' && (
                  <button
                    onClick={() => deleteEntry(p)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 10,
                      border: `1px solid ${t.danger}`,
                      background: 'transparent',
                      color: t.danger,
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div
          style={{
            marginTop: 14,
            padding: 14,
            border: `1px solid ${t.border}`,
            borderRadius: 12,
            background: t.cardAlt,
          }}
        >
          <div style={{ fontWeight: 800, marginBottom: 8 }}>
            {editing.isNew ? 'Add person' : `Edit: ${editing.name || '(no name)'}`}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: t.muted, marginBottom: 4 }}>
                Name {editing.role === 'self' && '(your own — fully editable; lookups are by member number)'}
              </div>
              <input
                type="text"
                value={editing.name}
                onChange={(e) => setEditing((cur) => ({ ...cur, name: e.target.value }))}
                placeholder="Full name as it appears on the booking"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: `1px solid ${t.inputBorder}`,
                  background: t.inputBg,
                  color: t.inputText,
                  fontSize: 14,
                  boxSizing: 'border-box',
                }}
              />
              {editing.role === 'self' && (
                <div style={{ fontSize: 12, color: t.muted, marginTop: 4 }}>
                  Tip: this should match how your name shows up on bookings — that's how the chef matches allergies to plates.
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <div style={{ fontSize: 12, color: t.muted, marginBottom: 4 }}>Role</div>
                <select
                  value={editing.role}
                  onChange={(e) => setEditing((cur) => ({ ...cur, role: e.target.value }))}
                  disabled={editing.role === 'self'}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: `1px solid ${t.inputBorder}`,
                    background: t.inputBg,
                    color: t.inputText,
                    fontSize: 14,
                  }}
                >
                  {ROLE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div style={{ fontSize: 12, color: t.muted, marginBottom: 4 }}>Severity</div>
                <select
                  value={editing.severity}
                  onChange={(e) => setEditing((cur) => ({ ...cur, severity: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: `1px solid ${t.inputBorder}`,
                    background: t.inputBg,
                    color: t.inputText,
                    fontSize: 14,
                  }}
                >
                  {SEVERITY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, color: t.muted, marginBottom: 4 }}>Allergies</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {CHIP_OPTIONS.map((opt) => {
                  const active = editing.allergies.includes(opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => toggleChip(opt)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 999,
                        border: active ? `2px solid ${t.accent}` : `1px solid ${t.inputBorder}`,
                        background: active ? t.accentSoft : t.inputBg,
                        color: t.text,
                        cursor: 'pointer',
                        fontWeight: 700,
                        fontSize: 13,
                      }}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, color: t.muted, marginBottom: 4 }}>
                Notes for the chef (severity, cross-contact, brand specifics)
              </div>
              <textarea
                rows={3}
                value={editing.note}
                onChange={(e) => setEditing((cur) => ({ ...cur, note: e.target.value }))}
                placeholder="e.g. Anaphylaxis to peanuts. Carries EpiPen. Cross-contact from fryers also reacts."
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: `1px solid ${t.inputBorder}`,
                  background: t.inputBg,
                  color: t.inputText,
                  fontSize: 14,
                  boxSizing: 'border-box',
                  resize: 'vertical',
                }}
                maxLength={500}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
            <button
              onClick={saveEntry}
              disabled={saving}
              style={{
                padding: '10px 16px',
                borderRadius: 10,
                border: 'none',
                background: t.ok,
                color: 'white',
                fontWeight: 800,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={cancelEdit}
              disabled={saving}
              style={{
                padding: '10px 16px',
                borderRadius: 10,
                border: `1px solid ${t.inputBorder}`,
                background: t.inputBg,
                color: t.text,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
